import { JSX, useEffect, useState } from 'react';
import IncidentReviewDetail from '../../views/IncidentReviewDetail';
import { useFormik } from 'formik';
import { getIncidentReviewDetailSchema } from './schema';
import IncidentReviewDetailStepOne from '../../views/IncidentReviewDetailStepOne';
import IncidentReviewDetailStepTwo from '../../views/IncidentReviewDetailStepTwo';
import { useApiClients } from '../../client/api/useApiClient';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CorrectiveActionsPriorityEnum,
  Department,
  DrafsCorrectiveActionResponse,
  GetAllDepartmentResponse,
  GetAllUsersListResponse,
  GetCorrectiveActionAssignedUsingIdResponse,
  GetIncidentByIdResponse,
  UserList,
} from '@EHSNavigator/backend';
import Loader from '../../components/Loader';
import moment from 'moment';
import CorrectiveActionCreated from '../../components/CorrectiveActionCreated';
import ModalComponent from '../../components/ModalComponent';
import { toggleModal } from '../../reducers/modalToggle';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useThrottleAsyncFn } from '../../utils/useThrottleAsyncFn';
import ErrorScreen from '../../components/ErrorScreen';

const INITIAL_STATE = {
  date: '',
  time: '',
  department: '',
  assignee: [],
  leadAssignee: '',
  actionPriority: '',
  actionTitle: '',
  description: '',
};
export default function IncidentReviewDetailPage() {
  const { IncidentReportingApi, DepartmentApi, UserApi, CorrectiveActionApi } =
    useApiClients();
  const modalName = useSelector(
    (state: { modalToggle: { modalName: string } }) =>
      state.modalToggle.modalName,
  );
  const dispatch = useDispatch();
  const params = useParams();
  const id = params['incident-review-id'] || '';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draft-id') ?? '';

  const { data, isLoading, refetch, error } = useQuery<
    GetIncidentByIdResponse['incident']
  >({
    queryKey: ['incident-review-by-id', id],
    queryFn: async () => {
      const response =
        await IncidentReportingApi.incidentReportingControllerFindIncidentById({
          id,
        });

      return response?.data?.incident;
    },
  });
  const { data: allDraftReviewIncident, isLoading: isAllDraftReviewLoading } =
    useQuery<DrafsCorrectiveActionResponse['correctiveActions']>({
      queryKey: ['review-all-drafts'],
      queryFn: async () => {
        const response =
          await CorrectiveActionApi.correctiveActionControllerGetDrafts();
        return response?.data.correctiveActions;
      },
    });
  const {
    data: draft,
    isLoading: draftLoading,
    isRefetching,
  } = useQuery<GetCorrectiveActionAssignedUsingIdResponse['correctiveAction']>({
    queryKey: ['get-corrective-is-draft-', draftId],
    queryFn: async () => {
      const response =
        await CorrectiveActionApi.correctiveActionControllerGetCorrectiveActionById(
          {
            correctiveActionId: draftId,
          },
        );
      return response?.data['correctiveAction'];
    },
    enabled: !!draftId,
    refetchOnWindowFocus: false,
  });

  const { data: allDepartments, isLoading: isDepartmentLoading } =
    useQuery<GetAllDepartmentResponse>({
      queryKey: ['departments'],
      queryFn: async () => {
        const response = await DepartmentApi.departmentControllerFindAllDepartment({
          disablePagination: true,
        });
        return response?.data;
      },
    });
  const { data: allUserList, isLoading: isUserListLoading } =
    useQuery<GetAllUsersListResponse>({
      queryKey: ['user-list'],
      queryFn: async () => {
        const response = await UserApi.userControllerGetUsersListForAssignee();
        return response?.data;
      },
    });
  const [currentStep, setCurrentStep] = useState<number>(draftId ? 2 : 1);

  const createCorrectiveActionMutation = useMutation({
    mutationFn: async (payload: any) => {
      const response =
        await CorrectiveActionApi.correctiveActionControllerCreate(payload);
      return response;
    },
    onSuccess: (data: any, variables: any) => {
      if (!variables?.createCorrectiveActionDto?.isDraft) {
        handleNextStep(1);
        dispatch(toggleModal(''));
      } else {
        navigate(`/incident-review/drafts/`);
        toast.success(data?.data?.message);
      }
    },
    onError: (error: any) => {
      dispatch(toggleModal(''));
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });

  const createCorrectiveActionUpdateMutation = useMutation({
    mutationFn: async (payload: any) => {
      return await CorrectiveActionApi.correctiveActionControllerUpdateDraft(
        payload,
      );
    },
    onSuccess: (data: any, variables: any) => {
      if (!variables?.createCorrectiveActionDto?.isDraft) {
        handleNextStep(1);
        dispatch(toggleModal(''));
      } else {
        navigate(`/incident-review/drafts/`);
        toast.success(data?.data?.message);
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });

  const handleNextStep = async (steps: number) => {
    if (steps < currentStep) {
      setCurrentStep(steps);
      window.scrollTo(0, 0);
    } else {
      const errors = await createCorrectiveActionHandler.validateForm();
      if (Object.keys(errors).length === 0) {
        setCurrentStep(steps);
        window.scrollTo(0, 0);
      } else {
        const touchedFields = Object.keys(errors).reduce((acc: any, key) => {
          acc[key] = true;
          return acc;
        }, {});
        createCorrectiveActionHandler.setTouched(touchedFields);
      }
    }
  };

  const createCorrectiveActionHandler = useFormik({
    initialValues: INITIAL_STATE,
    validationSchema: getIncidentReviewDetailSchema(currentStep),
    onSubmit: async (values) => {
      try {
        const payload = {
          isDraft: false,
          date: moment(values?.date).format('DD/MM/YYYY'),
          time: moment(values?.time, 'HH:mm A').format('hh:mmA'),
          departmentId: values.department,
          leadId:
            values.assignee?.length > 1
              ? values.leadAssignee
                ? values.leadAssignee
                : undefined
              : undefined,
          priority: values.actionPriority as CorrectiveActionsPriorityEnum,
          title: values.actionTitle,
          description: values.description,
          assigneeIds: values.assignee,
        };

        if (draftId) {
          await createCorrectiveActionUpdateMutation.mutateAsync({
            createCorrectiveActionDto: { ...payload },
            correctiveActionId: draft?.id,
            incidentId: id,
          });
        } else {
          await createCorrectiveActionMutation.mutateAsync({
            incidentId: id,
            createCorrectiveActionDto: { ...payload },
          });
        }

        await refetch();
        handleNextStep(1);
        dispatch(toggleModal('corrective-action-created'));
        createCorrectiveActionHandler?.resetForm();
      } catch (error: any) {
        console.error(error);
      }
    },
  });

  useEffect(() => {
    if (draft) {
      createCorrectiveActionHandler.setValues((prev: any) => ({
        ...prev,
        date: draft?.dueDate || '',
        time: draft?.dueTime || '',
        department: draft?.department?.id || '',
        assignee: draft?.assignees?.map((node) => node?.user?.id) || [],
        leadAssignee:
          (draft?.assignees || [])?.length > 1
            ? draft?.leadAssignee?.user?.id
            : '',
        actionPriority: draft?.priority || '',
        actionTitle: draft?.title || '',
        description: draft?.description || '',
      }));
    }
  }, [draft]);

  async function handleSaveAsDraft() {
    const { values, setFieldError, setTouched } = createCorrectiveActionHandler;

    if (values.actionTitle?.trim() === '') {
      setFieldError('title', 'Title is required');
      setTouched({ actionTitle: true }, true);
      return;
    }
    const cleanedData = {
      isDraft: true,
      date: values?.date ? moment(values?.date).format('DD/MM/YYYY') : null,
      time: values?.time
        ? moment(values?.time, 'HH:mm A').format('hh:mmA')
        : null,
      departmentId: values.department,
      leadId: values.leadAssignee ? values.leadAssignee : undefined,
      priority: values.actionPriority,
      title: values.actionTitle,
      description: values.description,
      assigneeIds: values.assignee,
    };

    const removeEmptyValues = Object.fromEntries(
      Object.entries(cleanedData).filter(([, value]) => {
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'boolean') return value;
        return value !== '' && value !== null && value !== undefined;
      }),
    );

    if (draftId) {
      await createCorrectiveActionUpdateMutation.mutateAsync({
        createCorrectiveActionDto: { ...removeEmptyValues },
        correctiveActionId: draft?.id,
        incidentId: id,
      });
    } else {
      await createCorrectiveActionMutation.mutateAsync({
        incidentId: id,
        createCorrectiveActionDto: { ...removeEmptyValues },
      });
    }
  }
  const throttledSaveAsDraft = useThrottleAsyncFn(handleSaveAsDraft, 3000);

  function areObjectsEqual(obj1: any, obj2: any) {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) return false;
    return keys1.every((key) => obj1[key] === obj2[key]);
  }

  const isFormUnchanged = areObjectsEqual(
    {
      date: moment(createCorrectiveActionHandler?.values?.date).format(
        'DD/MM/YYYY',
      ),
      time: moment(
        createCorrectiveActionHandler?.values?.time,
        'HH:mm A',
      ).format('hh:mmA'),
      departmentId: createCorrectiveActionHandler?.values.department,
      leadId:
        createCorrectiveActionHandler?.values.assignee?.length > 1
          ? createCorrectiveActionHandler?.values.leadAssignee &&
            createCorrectiveActionHandler?.values.leadAssignee
          : '',
      priority: createCorrectiveActionHandler?.values.actionPriority,
      title: createCorrectiveActionHandler?.values.actionTitle,
      description: createCorrectiveActionHandler?.values.description,
      assigneeIds: JSON.stringify(
        createCorrectiveActionHandler?.values.assignee || [],
      ),
    },
    {
      date: moment(draft?.dueDate).format('DD/MM/YYYY'),
      time: moment(draft?.dueTime, 'HH:mm A').format('hh:mmA'),
      departmentId: draft?.department ? draft?.department?.id : '',
      leadId:
        (draft?.assignees || [])?.length > 1
          ? draft?.leadAssignee?.user?.id
          : '',
      priority: draft?.priority ? draft?.priority : '',
      title: draft?.title ? draft?.title : '',
      description: draft?.description ? draft?.description : '',
      assigneeIds: JSON.stringify(
        (draft?.assignees || [])?.map((node) => node?.user?.id),
      ),
    },
  );

  const departments =
    allDepartments?.departments?.map((node: Department) => ({
      label: node?.name,
      value: node?.id,
    })) || [];
  const userList =
    allUserList?.users?.map((node: UserList) => ({
      label: node?.fullName,
      value: node?.id,
    })) || [];

  const steps: Record<number, JSX.Element> = {
    1: (
      <IncidentReviewDetail
        data={data as GetIncidentByIdResponse['incident']}
        handleSteps={handleNextStep}
        refetch={refetch}
      />
    ),
    2: (
      <IncidentReviewDetailStepOne
        data={data as GetIncidentByIdResponse['incident']}
        formHandler={createCorrectiveActionHandler}
        handleSteps={handleNextStep}
        departments={departments}
        userList={userList}
        allDraftIncidentReviewsLength={
          (allDraftReviewIncident || [])?.length > 0
            ? allDraftReviewIncident?.length
            : undefined
        }
        handleDraft={throttledSaveAsDraft}
        isDraftLoading={
          isFormUnchanged ||
          createCorrectiveActionHandler?.values?.actionTitle === '' ||
          createCorrectiveActionMutation?.isPending ||
          createCorrectiveActionUpdateMutation?.isPending
        }
        disbaleButtons={
          createCorrectiveActionMutation?.isPending ||
          createCorrectiveActionUpdateMutation?.isPending
        }
      />
    ),
    3: (
      <IncidentReviewDetailStepTwo
        formHandler={createCorrectiveActionHandler}
        handleSteps={handleNextStep}
        departments={departments}
        userList={userList}
        data={data as GetIncidentByIdResponse['incident']}
        allDraftIncidentReviewsLength={
          (allDraftReviewIncident || [])?.length > 0
            ? allDraftReviewIncident?.length
            : undefined
        }
        handleDraft={throttledSaveAsDraft}
        isDraftLoading={
          isFormUnchanged ||
          createCorrectiveActionHandler?.values?.actionTitle === '' ||
          createCorrectiveActionMutation?.isPending ||
          createCorrectiveActionUpdateMutation?.isPending
        }
        disbaleButtons={
          createCorrectiveActionMutation?.isPending ||
          createCorrectiveActionUpdateMutation?.isPending
        }
      />
    ),
  };

  if (
    isLoading ||
    isDepartmentLoading ||
    isUserListLoading ||
    draftLoading ||
    isAllDraftReviewLoading ||
    isRefetching
  ) {
    return <Loader />;
  }

  if (error) {
    return <ErrorScreen />;
  }
  return (
    <div className="flex w-full flex-1 flex-col px-15 pt-14 pb-10 md:px-25 lg:pr-14 lg:pb-20 lg:pl-8">
      {steps[currentStep]}
      {modalName === 'corrective-action-created' && (
        <ModalComponent
          customClose={() => {
            dispatch(toggleModal(''));
          }}
          otherClasses="px-4 sm:px-0 "
          additionalClass="w-full max-w-465 rounded-2xl p-5 md:py-20 md:px-8"
        >
          <CorrectiveActionCreated />
        </ModalComponent>
      )}
    </div>
  );
}
